import { ChatListItemAvatar } from "@features/messaging/components/ChatListItemAvatar";
import { ChatListItemContent } from "@features/messaging/components/ChatListItemContent";
import { formatChatListTime, truncateChatMessage } from "@features/messaging/utils/chatListItemUtils";
import type { Chat } from "@fitties/shared";

import { motion } from "framer-motion";
import { ChevronRight } from "lucide-react";

/**
 * Props interface for the ChatListItem component.
 */
interface ChatListItemProps {
  /** Chat conversation data */
  chat?: Chat;
  /** Callback when chat item is clicked */
  onClick: () => void;
  /** Whether this chat is unread */
  isUnread?: boolean;
  /** Last message time */
  lastMessageTime?: Date;
  /** Other user's display name */
  otherUserName: string;
  /** Other user's photo URL */
  otherUserPhoto?: string | null;
  /** Other user's uid (for profile navigation) */
  otherUserUid?: string;
  /** Callback when profile/avatar is clicked */
  onProfileClick?: (userId: string) => void;
  /** Whether the chat row is visible but locked */
  isLocked?: boolean;
}

/**
 * ChatListItem component for displaying chat conversations.
 * Composes ChatListItemAvatar and ChatListItemContent.
 */
export function ChatListItem({
  chat,
  onClick,
  isUnread = false,
  lastMessageTime,
  otherUserName,
  otherUserPhoto,
  otherUserUid,
  onProfileClick,
  isLocked = false,
}: ChatListItemProps) {
  const displayName = otherUserName;
  const timeLabel = isLocked ? "" : formatChatListTime(lastMessageTime ?? chat?.last_message_sent_timestamp);
  const previewText = isLocked ? "Unlock to chat" : truncateChatMessage(chat?.last_message_text);

  return (
    <motion.div
      className={`flex w-full items-center gap-3 p-4 text-left transition-colors hover:bg-gray-50 ${isLocked ? "opacity-70" : ""}`}
      whileHover={{ backgroundColor: "rgba(249, 250, 251, 1)" }}
      data-testid="chat-list-item"
    >
      <ChatListItemAvatar
        displayName={displayName}
        photoUrl={otherUserPhoto}
        onClick={otherUserUid && onProfileClick ? () => onProfileClick(otherUserUid) : undefined}
      />

      <button
        type="button"
        onClick={onClick}
        className="flex min-w-0 flex-1 items-center gap-3 text-left hover:bg-transparent"
        aria-label={`Chat with ${displayName}`}
        data-testid="chat-list-item-open-chat"
      >
        <ChatListItemContent
          displayName={displayName}
          timeLabel={timeLabel}
          previewText={previewText}
          isUnread={isLocked ? false : isUnread}
          isLocked={isLocked}
        />
        <div className="ml-2 flex-shrink-0">
          <ChevronRight className="h-5 w-5 text-gray-400" aria-hidden="true" />
        </div>
      </button>
    </motion.div>
  );
}
